import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import { ComponentPublicInstance } from "vue";
import { useToastsStore } from "@stores/toasts";
import { MetricSender } from "@/helpers/MetricSender";
import { getErrorMessage, checkIsNetworkErrorFromMessage } from "@/logic/ErrorHelper";
import { isKnownExternalError, shouldFullySuppressError, shouldShowUserError } from "./KnownExternalErrors";

export function processUnhandledError(error: unknown, errorHandler: string, vm: ComponentPublicInstance | null = null) {
  const remoteLogger = getGlobalRemoteLogger();

  try {
    const errorMessageDetails = getErrorMessage(error);
    const componentName = vm?.$options?.name || vm?.$?.type?.__name || vm?.$?.type?.name || "<unknown>";
    const stack = error instanceof Error ? error.stack : undefined;
    if (isKnownExternalError(errorMessageDetails, stack)) return;
    const isNetworkError = checkIsNetworkErrorFromMessage(errorMessageDetails);
    // Note: Some places have specific conflict handling code, but this catches anywhere that doesn't and has strict conflict handling on.
    const isConflictError = errorMessageDetails?.toLowerCase()?.includes("status code 409") === true;

    if (shouldFullySuppressError(errorMessageDetails)) {
      console.info("Suppressing noisy error", errorMessageDetails);
      return;
    }

    // filter out errors that should not be shown to the user.
    // ResizeObserver errors are noisy and not easy to track down.
    const displayErrorToUser = shouldShowUserError(errorMessageDetails);

    const currentUri = window.location.href;
    const category = !displayErrorToUser ? "filtered" : isNetworkError ? "network" : isConflictError ? "conflict" : "error";
    remoteLogger.error(`ErrorToast! group=${category}, error=${errorMessageDetails}`, true, false, {
      category: category,
      networkError: isNetworkError,
      showUser: displayErrorToUser,
      uri: currentUri,
      errorHandler: errorHandler,
      component: componentName,
      stacktrace: stack,
    });

    if (!displayErrorToUser) return;

    const toastsStore = useToastsStore();
    /* if (import.meta.env.DEV) {
      toastsStore.error({ message, details, stack });
    } else {*/
    // if the error is a network error, we can provide a more helpful message
    let details = "";
    let messageToUser = "Oops, something didn't work";
    if (isNetworkError) {
      messageToUser = "Network Error";
      details = "Your network seems to be having issues.\n";
    }
    details += "Please try again or contact support@shrpa.com";

    if (isConflictError) {
      messageToUser = "Data Conflict";
      details = "This data was updated since you last loaded.\nPlease refresh and try again or contact support@shrpa.com";
    }
    if (isNetworkError || isConflictError) {
      toastsStore.warn({
        message: messageToUser,
        details,
      });
      MetricSender.sendMetric("Client-NetworkError", null);
    } else {
      toastsStore.error({
        message: messageToUser,
        details,
      });
      MetricSender.sendMetric("Client-ErrorShown", null);
      MetricSender.setReplayPriority(1);
    }
  } catch (e) {
    remoteLogger.error(`FATAL ERROR: Error handling error. ${e} ${e.stack}`, true, true);
  }

  // If running locally, re-throw so we get more console info
  /* if (window.location.hostname === "localhost") {
    throw error;
  }*/
}
